/**
 * SafetyNow Data Service — API-first with localStorage fallback.
 * All pages use this instead of direct localStorage calls.
 * When the backend is available, data syncs to SQLite.
 * When offline, falls back to localStorage seamlessly.
 *
 * For NEW API calls, prefer apiFetch from hooks/useApi. This file remains
 * for the offline-fallback pattern. See .kiro/steering/frontend-conventions.md.
 */

import { API_BASE as API, authHeaders } from '../hooks/useApi';
let apiAvailable: boolean | null = null;

async function checkApi(): Promise<boolean> {
  if (apiAvailable !== null) return apiAvailable;
  try {
    const r = await fetch('/health', { signal: AbortSignal.timeout(1000) });
    apiAvailable = r.ok;
  } catch { apiAvailable = false; }
  // Re-check every 60s
  setTimeout(() => { apiAvailable = null; }, 60000);
  return apiAvailable;
}

// Eagerly check on load so pages don't wait
checkApi();

// ─── FTA ───────────────────────────────────────────────────────────────────

export async function listFTAs(projectId?: string): Promise<any[]> {
  if (await checkApi()) {
    try {
      const url = projectId ? `${API}/fta?projectId=${projectId}` : `${API}/fta`;
      const r = await fetch(url, { headers: authHeaders(), signal: AbortSignal.timeout(5000) });
      if (r.ok) {
        const json = await r.json();
        return json.data || [];
      }
    } catch { /* fall through */ }
  }
  // Fallback: localStorage
  return Object.keys(localStorage)
    .filter(k => k.startsWith('fta:'))
    .map(k => { try { return JSON.parse(localStorage.getItem(k)!); } catch { return null; } })
    .filter(Boolean);
}

export async function loadFTA(id: string): Promise<any | null> {
  if (await checkApi()) {
    try {
      const r = await fetch(`${API}/fta/${id}`, { headers: authHeaders(), signal: AbortSignal.timeout(5000) });
      if (r.ok) { const json = await r.json(); return json.data; }
    } catch { /* fall through */ }
  }
  const raw = localStorage.getItem(`fta:${id}`);
  return raw ? JSON.parse(raw) : null;
}

export async function saveFTA(projectId: string, name: string, nodes: any[], links: any[], ftaId?: string): Promise<string> {
  const gates = nodes.filter((n: any) => ['AND','OR','VOTE','INHIBIT','NOT','XOR','TRANSFER'].includes(n.category))
    .map((n: any) => ({ id: n.id || n.key, type: n.category, description: n.label || '', inputs: [] }));
  const events = nodes.filter((n: any) => ['BASIC','UNDEVELOPED','HOUSE'].includes(n.category) || !n.category || n.category === 'Event')
    .map((n: any) => ({ id: n.id || n.key, type: 'basic', description: n.label || '', rate: n.lambda || n.q || 0 }));

  // Always save to localStorage
  const lsData = { name, nodes, links };
  localStorage.setItem(`fta:${name}`, JSON.stringify(lsData));

  if (await checkApi()) {
    try {
      if (ftaId) {
        await fetch(`${API}/fta/${ftaId}`, {
          method: 'PUT', headers: authHeaders(),
          body: JSON.stringify({ name, gates, events }),
          signal: AbortSignal.timeout(5000),
        });
        return ftaId;
      } else {
        const r = await fetch(`${API}/fta`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ projectId, name, gates, events }),
          signal: AbortSignal.timeout(5000),
        });
        if (r.ok) { const json = await r.json(); return json.data?.id || name; }
      }
    } catch { /* localStorage already saved */ }
  }
  return name;
}

/**
 * Load the lossless persisted artifact envelope. Unlike `loadFTA`, this
 * deliberately has no localStorage fallback: callers selected a server row
 * and must fail visibly rather than reconstructing a different model.
 */
export async function loadPersistedFTAArtifact(id: string): Promise<any> {
  const r = await fetch(`${API}/fta/artifacts/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
    signal: AbortSignal.timeout(5000),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Unable to load persisted FTA (${r.status}).`);
  }
  const body = await r.json();
  if (!body?.data) throw new Error('Persisted FTA response did not contain artifact data.');
  return body.data;
}

/** Load one immutable persisted analysis run by its server id. */
export async function loadFTAAnalysisRun(runId: string): Promise<any> {
  const r = await fetch(`${API}/fta/analysis-runs/${encodeURIComponent(runId)}`, {
    headers: authHeaders(),
    signal: AbortSignal.timeout(5000),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Unable to load FTA analysis run (${r.status}).`);
  }
  const body = await r.json();
  if (!body?.data) throw new Error('FTA analysis-run response did not contain run data.');
  return body.data;
}

// ─── FMEA ──────────────────────────────────────────────────────────────────

export async function listFMEAWorksheets(projectId?: string): Promise<any[]> {
  if (await checkApi()) {
    try {
      const url = projectId ? `${API}/fmea/worksheets?projectId=${projectId}` : `${API}/fmea/worksheets`;
      const r = await fetch(url, { headers: authHeaders(), signal: AbortSignal.timeout(5000) });
      if (r.ok) { const json = await r.json(); return json.data || []; }
    } catch { /* fall through */ }
  }
  const raw = localStorage.getItem('fmea:data');
  return raw ? [JSON.parse(raw)] : [];
}

export async function saveFMEAWorksheet(projectId: string, entries: any[], wsId?: string): Promise<string> {
  localStorage.setItem('fmea:data', JSON.stringify({ entries }));
  if (await checkApi()) {
    try {
      if (wsId) {
        await fetch(`${API}/fmea/worksheets/${wsId}`, {
          method: 'PUT', headers: authHeaders(),
          body: JSON.stringify({ entries }),
          signal: AbortSignal.timeout(5000),
        });
        return wsId;
      } else {
        const r = await fetch(`${API}/fmea/worksheets`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ projectId, entries }),
          signal: AbortSignal.timeout(5000),
        });
        if (r.ok) { const json = await r.json(); return json.data?.id || 'local'; }
      }
    } catch { /* localStorage saved */ }
  }
  return 'local';
}

// ─── Assessments (AFHA/PASA/SFHA/PSSA/SSA/ASA) ────────────────────────────

export async function listAssessments(projectId?: string): Promise<any[]> {
  if (await checkApi()) {
    try {
      const url = projectId ? `${API}/assessments?projectId=${projectId}` : `${API}/assessments`;
      const r = await fetch(url, { headers: authHeaders(), signal: AbortSignal.timeout(5000) });
      if (r.ok) { const json = await r.json(); return json.data || []; }
    } catch { /* fall through */ }
  }
  const raw = localStorage.getItem('hazard-assessment');
  return raw ? JSON.parse(raw) : [];
}

export async function saveAssessment(projectId: string, type: string, entries: any[], asmntId?: string): Promise<string> {
  if (await checkApi()) {
    try {
      if (asmntId) {
        await fetch(`${API}/assessments/${asmntId}`, {
          method: 'PUT', headers: authHeaders(),
          body: JSON.stringify({ entries }),
          signal: AbortSignal.timeout(5000),
        });
        return asmntId;
      } else {
        const r = await fetch(`${API}/assessments`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ projectId, type, entries }),
          signal: AbortSignal.timeout(5000),
        });
        if (r.ok) { const json = await r.json(); return json.data?.id || 'local'; }
      }
    } catch { /* fall through */ }
  }
  return 'local';
}

// ─── Traceability Links ────────────────────────────────────────────────────

export async function listLinks(artifactId?: string): Promise<any[]> {
  if (await checkApi()) {
    try {
      const url = artifactId ? `${API}/assessments/links?artifactId=${artifactId}` : `${API}/assessments/links`;
      const r = await fetch(url, { headers: authHeaders(), signal: AbortSignal.timeout(5000) });
      if (r.ok) { const json = await r.json(); return json.data || []; }
    } catch { /* fall through */ }
  }
  return [];
}

export async function createLink(sourceId: string, targetId: string, linkType: string): Promise<string> {
  if (await checkApi()) {
    try {
      const r = await fetch(`${API}/assessments/links`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ sourceId, targetId, linkType }),
        signal: AbortSignal.timeout(5000),
      });
      if (r.ok) { const json = await r.json(); return json.data?.id || ''; }
    } catch { /* fall through */ }
  }
  return '';
}

// ─── Connection status ─────────────────────────────────────────────────────

export async function isApiConnected(): Promise<boolean> {
  return checkApi();
}
